import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  deactivateTicketLabel,
  getApiErrorMessage,
  listTicketLabels,
} from '../../api/client';
import type { TicketLabelDefinition } from '../../api/types';
import Badge from '../../components/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { canEditPlatformConfig, canWritePlatformConfig } from '../../utils/permissions';
import { formatLabel } from '../../utils/ticket';
import { PLATFORM_CONFIG } from '../platform-config/paths';

export default function TicketLabelsPage() {
  const { user } = useAuth();
  const [labels, setLabels] = useState<TicketLabelDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const canCreate = user && canWritePlatformConfig(user);
  const canModify = user && canEditPlatformConfig(user);

  useEffect(() => {
    listTicketLabels()
      .then(setLabels)
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  async function handleDeactivate(id: string, name: string) {
    if (!window.confirm(`Deactivate label "${name}"? Existing tickets keep it; new tickets cannot use it.`)) {
      return;
    }
    try {
      const updated = await deactivateTicketLabel(id);
      setLabels((prev) => prev.map((l) => (l.id === id ? updated : l)));
    } catch (err) {
      setError(getApiErrorMessage(err));
    }
  }

  return (
    <>
      {canCreate && (
        <div className="page-header" style={{ marginTop: 0 }}>
          <div />
          <Link to={PLATFORM_CONFIG.labelsNew} className="btn btn-primary">
            Add label
          </Link>
        </div>
      )}
      {error && <div className="alert alert-error">{error}</div>}
      <div className="card">
        {loading ? (
          <div className="loading">Loading labels…</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Tickets</th>
                <th>Status</th>
                {canModify && <th />}
              </tr>
            </thead>
            <tbody>
              {labels.map((l) => (
                <tr key={l.id}>
                  <td>
                    {canModify ? <Link to={PLATFORM_CONFIG.label(l.id)}>{l.code}</Link> : l.code}
                    {l.isSystem && (
                      <>
                        {' '}
                        <Badge label="SYSTEM" className="badge-system" />
                      </>
                    )}
                    {l.isDefault && (
                      <>
                        {' '}
                        <Badge label="DEFAULT" className="badge-neutral" />
                      </>
                    )}
                  </td>
                  <td>{formatLabel(l)}</td>
                  <td>{l._count?.assignments ?? '—'}</td>
                  <td>
                    <Badge
                      label={l.isActive ? 'ACTIVE' : 'INACTIVE'}
                      className={l.isActive ? 'badge-success' : 'badge-neutral'}
                    />
                  </td>
                  {canModify && (
                    <td>
                      {l.isActive && (
                        <button
                          type="button"
                          className="btn btn-danger-ghost"
                          onClick={() => void handleDeactivate(l.id, l.name)}
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
